import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { userService } from '../../services/user.service';
import { FaSearch, FaTrash, FaUsers, FaUserCheck, FaUserClock, FaUserTimes } from 'react-icons/fa';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');

    const [stats, setStats] = useState({
        all: 0,
        active: 0,
        pending: 0,
        inactive: 0
    });

    useEffect(() => {
        fetchUsers();
    }, [filter]);

    const fetchUsers = async () => {
        try {
            setLoading(true);

            const params = filter !== 'all' ? { status: filter } : {};
            const data = await userService.getAllUsers(params);

            console.log("FULL RESPONSE:", data);

            // ✅ FIXED
            const usersData = data.users || [];

            console.log("USERS:", usersData);
            console.log("FIRST USER:", usersData[0]);

            setUsers(usersData);

            setStats({
                all: usersData.length,
                active: usersData.filter(u => u.status === 'active').length,
                pending: usersData.filter(u => u.status === 'pending').length,
                inactive: usersData.filter(u => u.status === 'inactive').length
            });

        } catch (error) {
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            await userService.deleteUser(id);
            toast.success('User deleted');
            fetchUsers();
        } catch {
            toast.error('Failed to delete user');
        }
    };

    // search filter
    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusStyle = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-600';
            case 'pending': return 'bg-yellow-100 text-yellow-600';
            case 'inactive': return 'bg-red-100 text-red-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <div className="p-6">

            {/* HEADER */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold">User Management</h1>
                <p className="text-gray-600 mt-2">Manage system users</p>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <Stat title="Total Users" value={stats.all} icon={<FaUsers />} />
                <Stat title="Active" value={stats.active} icon={<FaUserCheck />} />
                <Stat title="Pending" value={stats.pending} icon={<FaUserClock />} />
                <Stat title="Inactive" value={stats.inactive} icon={<FaUserTimes />} />
            </div>

            {/* SEARCH + FILTER */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex gap-4">
                    <div className="flex-1 relative">
                        <FaSearch className="absolute left-3 top-3 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        />
                    </div>

                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="px-4 py-2 border rounded-lg"
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-lg shadow overflow-hidden">

                {loading ? (
                    <div className="text-center py-10">Loading...</div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        No users found
                    </div>
                ) : (
                    <table className="min-w-full">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-6 py-3 text-left">Name</th>
                                <th className="px-6 py-3 text-left">Email</th>
                                <th className="px-6 py-3 text-left">Role</th>
                                <th className="px-6 py-3 text-left">Status</th>
                                <th className="px-6 py-3 text-left">Actions</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {filteredUsers.map((user) => (
                                <tr key={user._id} className="hover:bg-gray-50">

                                    <td className="px-6 py-4 font-medium">{user.name}</td>

                                    <td className="px-6 py-4 text-gray-600">{user.email}</td>

                                    {/* ✅ FIXED ROLE */}
                                    <td className="px-6 py-4">
                                        {typeof user.role === 'string'
                                            ? user.role
                                            : user.role?.displayName || user.role?.name || 'N/A'}
                                    </td>

                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs ${getStatusStyle(user.status)}`}>
                                            {user.status}
                                        </span>
                                    </td>

                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => handleDelete(user._id)}
                                            className="p-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

            </div>
        </div>
    );
};

// STAT CARD
const Stat = ({ title, value, icon }) => (
    <div className="bg-white p-6 rounded-lg shadow flex justify-between items-center">
        <div>
            <p className="text-gray-500 text-sm">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className="text-3xl text-blue-500">{icon}</div>
    </div>
);

export default Users;